import { useMemo } from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import rehypeKatex from "rehype-katex";
import rehypeSlug from "rehype-slug";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import { MermaidDiagram } from "./MermaidDiagram";
import { createRemarkWikiLink } from "../utils/remarkWikiLink";
import { PreviewFont, PreviewWidth } from "../types";

interface MarkdownPreviewProps {
  markdown: string;
  darkMode: boolean;
  fontSize: number;
  width: PreviewWidth;
  font: PreviewFont;
  enableMath: boolean;
  /** Names of every note in the vault — lets unresolved `[[links]]` render as "new". */
  noteNames?: string[];
  /** Open (or create) the note a `[[wikilink]]` points at. */
  onWikiLink?: (name: string) => void;
  /** Filter the vault by a `#tag` chip. */
  onTagClick?: (tag: string) => void;
}

const widthValues: Record<PreviewWidth, string> = {
  compact: "660px",
  comfortable: "820px",
  wide: "100%",
};

// Let our internal note/tag schemes survive react-markdown's URL sanitiser;
// everything else still goes through the default (which strips javascript: etc.).
const urlTransform = (url: string) =>
  url.startsWith("wiki:") || url.startsWith("tag:") ? url : defaultUrlTransform(url);

export const MarkdownPreview = ({
  markdown,
  darkMode,
  fontSize,
  width,
  font,
  enableMath,
  noteNames,
  onWikiLink,
  onTagClick,
}: MarkdownPreviewProps) => {
  // Math plugins are opt-in: $-delimiters can collide with literal dollar signs
  // (currency), so users can turn them off in Settings.
  const remarkPlugins = useMemo(() => {
    const wikiLink = createRemarkWikiLink(new Set((noteNames ?? []).map((name) => name.toLowerCase())));
    return enableMath ? [remarkGfm, remarkMath, wikiLink] : [remarkGfm, wikiLink];
  }, [enableMath, noteNames]);
  const rehypePlugins = useMemo(
    () => (enableMath ? [rehypeSlug, rehypeKatex, rehypeHighlight] : [rehypeSlug, rehypeHighlight]),
    [enableMath],
  );

  return (
  <article
    className={font === "serif" ? "markdown-body preview-serif" : "markdown-body"}
    // Custom properties (not direct values) so the PDF export stylesheet can
    // still override the consuming declarations.
    style={{
      ["--preview-font-size" as string]: `${fontSize}px`,
      ["--preview-max-width" as string]: widthValues[width],
    }}
  >
    <ReactMarkdown
      remarkPlugins={remarkPlugins}
      rehypePlugins={rehypePlugins}
      urlTransform={urlTransform}
      components={{
        code({ className, children, ...props }) {
          const language = /language-(\w+)/.exec(className ?? "")?.[1];
          const code = String(children).replace(/\n$/, "");

          if (language === "mermaid") {
            return <MermaidDiagram chart={code} darkMode={darkMode} />;
          }

          return (
            <code className={className} {...props}>
              {children}
            </code>
          );
        },
        a({ href, className, children, ...props }) {
          if (href?.startsWith("wiki:")) {
            const name = decodeURIComponent(href.slice(5));
            return (
              <a
                className={className}
                href={`#${href}`}
                onClick={(event) => {
                  event.preventDefault();
                  onWikiLink?.(name);
                }}
                {...props}
              >
                {children}
              </a>
            );
          }
          if (href?.startsWith("tag:")) {
            const tag = decodeURIComponent(href.slice(4));
            return (
              <a
                className={className}
                href={`#${href}`}
                onClick={(event) => {
                  event.preventDefault();
                  onTagClick?.(tag);
                }}
                {...props}
              >
                {children}
              </a>
            );
          }
          return (
            <a href={href} className={className} {...props}>
              {children}
            </a>
          );
        },
      }}
    >
      {markdown}
    </ReactMarkdown>
  </article>
  );
};
